import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  mockScheduledMeetings, mockMeetingTemplates, mockMeetingAnalytics,
  mockCommittees, mockUsers, meetingStatuses
} from '../mock/data';
import { 
  Search, Filter, Plus, Calendar, Clock, MapPin, Video, Users, FileText,
  ChevronDown, ChevronRight, Settings, MoreVertical, Edit, Trash2, Send,
  CheckCircle2, AlertCircle, XCircle, Play, Pause, Check, X, Copy,
  Sparkles, ListTodo, BarChart3, TrendingUp, CalendarDays, UserCheck,
  ClipboardList, Mail, Link2, ExternalLink, RefreshCw, Eye, Download,
  MessageSquare, ThumbsUp, ArrowRight, Zap, Target, Award, Timer,
  LayoutGrid, List, CalendarRange, GanttChart, GripVertical, UserPlus,
  Building2, Globe, Phone, AtSign, Save, ArrowUpRight, Undo2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import HeaderGlow from '../components/HeaderGlow';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Format time helper
const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Status badge config
const getStatusConfig = (status) => {
  const configs = {
    'draft': { label: 'Draft', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Edit },
    'scheduled': { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Calendar },
    'in-progress': { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Play },
    'pending-review': { label: 'Pending Review', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertCircle },
    'approved': { label: 'Approved', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
    'completed': { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Check },
    'cancelled': { label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
  };
  return configs[status] || configs['draft'];
};

// View options
const VIEW_OPTIONS = [
  { id: 'cards', label: 'Cards', icon: LayoutGrid },
  { id: 'table', label: 'Table', icon: List },
  { id: 'calendar', label: 'Calendar', icon: CalendarRange },
  { id: 'timeline', label: 'Timeline', icon: GanttChart }
];

// Generate mock video links
const generateMeetingLink = (provider) => {
  const id = Math.random().toString(36).substring(2, 12);
  if (provider === 'zoom') return `https://zoom.us/j/${id}`;
  if (provider === 'teams') return `https://teams.microsoft.com/l/meetup-join/${id}`;
  if (provider === 'meet') return `https://meet.google.com/${id}`;
  return '';
};

const MeetingManagement = ({ 
  showDashboard = false,
  viewMode = 'all',
  showCalendar = false,
  showTemplates = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const { currentColorTheme } = useTheme();
  const { collapsed } = useSidebar();
  
  // View state
  const [currentView, setCurrentView] = useState('cards');
  
  // Filter states
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  
  // Dialog states
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [showMeetingDetails, setShowMeetingDetails] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  
  // Meeting detail edit states
  const [editingAgenda, setEditingAgenda] = useState(false);
  const [editingAttendees, setEditingAttendees] = useState(false);
  const [editingMOM, setEditingMOM] = useState(false);
  
  // New meeting form
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    committeeId: '',
    templateId: '',
    date: '',
    startTime: '',
    duration: 60,
    location: '',
    videoProvider: '',
    videoLink: '',
    isRecurring: false,
    recurringFrequency: 'weekly'
  });

  // New attendee form
  const [newAttendee, setNewAttendee] = useState({
    name: '',
    email: '',
    role: 'Member',
    isExternal: false,
    company: ''
  });

  // New agenda item form
  const [newAgendaItem, setNewAgendaItem] = useState({
    title: '',
    duration: 15,
    presenter: ''
  });

  // MOM editor state
  const [momContent, setMomContent] = useState('');

  // Local state for meetings
  const [meetings, setMeetings] = useState(mockScheduledMeetings);
  const [templates, setTemplates] = useState(mockMeetingTemplates);

  // Filter meetings based on viewMode and filters
  const filteredMeetings = useMemo(() => {
    let result = [...meetings];
    
    const today = new Date().toISOString().split('T')[0];
    if (viewMode === 'upcoming') {
      result = result.filter(m => m.date >= today && !['completed', 'cancelled'].includes(m.status));
    } else if (viewMode === 'past') {
      result = result.filter(m => m.date < today || ['completed', 'cancelled'].includes(m.status));
    } else if (viewMode === 'pending-approval') {
      result = result.filter(m => m.status === 'pending-review');
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.title.toLowerCase().includes(query) ||
        mockCommittees.find(c => c.id === m.committeeId)?.name.toLowerCase().includes(query)
      );
    }
    
    if (selectedTypes.length > 0) {
      result = result.filter(m => selectedTypes.includes(m.committeeId));
    }
    
    if (selectedStatuses.length > 0) {
      result = result.filter(m => selectedStatuses.includes(m.status));
    }
    
    result.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return result;
  }, [meetings, viewMode, searchQuery, selectedTypes, selectedStatuses]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const total = meetings.length;
    const upcoming = meetings.filter(m => m.date >= new Date().toISOString().split('T')[0] && !['completed', 'cancelled'].includes(m.status)).length;
    const pendingApproval = meetings.filter(m => m.status === 'pending-review').length;
    const completed = meetings.filter(m => m.status === 'completed').length;
    const totalActions = meetings.reduce((acc, m) => acc + (m.actions?.length || 0), 0);
    const completedActions = meetings.reduce((acc, m) => acc + (m.actions?.filter(a => a.status === 'completed').length || 0), 0);
    
    return {
      total,
      upcoming,
      pendingApproval,
      completed,
      totalActions,
      completedActions,
      actionCompletionRate: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0
    };
  }, [meetings]);

  // Get page title
  const getPageTitle = () => {
    if (showDashboard) return { title: 'Meeting Dashboard', subtitle: 'Overview of all meetings and activities' };
    if (showCalendar) return { title: 'Meeting Calendar', subtitle: 'Visual calendar view of all meetings' };
    if (showTemplates) return { title: 'Meeting Templates', subtitle: 'Manage reusable meeting templates' };
    if (viewMode === 'upcoming') return { title: 'Upcoming Meetings', subtitle: 'Scheduled meetings that are coming up' };
    if (viewMode === 'past') return { title: 'Past Meetings', subtitle: 'Completed and archived meetings' };
    if (viewMode === 'pending-approval') return { title: 'Pending Approval', subtitle: 'Meetings awaiting MOM approval' };
    return { title: 'All Meetings', subtitle: 'Manage all your meetings' };
  };
  const pageInfo = getPageTitle();

  // Calculate end time
  const calculateEndTime = (startTime, duration) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  // Handle create meeting
  const handleCreateMeeting = () => {
    const template = newMeeting.templateId && newMeeting.templateId !== 'none' 
      ? templates.find(t => t.id === newMeeting.templateId) 
      : null;
    const videoLink = newMeeting.videoProvider && newMeeting.videoProvider !== 'none' 
      ? generateMeetingLink(newMeeting.videoProvider) 
      : '';
    
    const newMeetingData = {
      id: `meeting-${Date.now()}`,
      title: newMeeting.title,
      committeeId: newMeeting.committeeId,
      templateId: newMeeting.templateId,
      date: newMeeting.date,
      startTime: newMeeting.startTime,
      endTime: calculateEndTime(newMeeting.startTime, newMeeting.duration),
      duration: newMeeting.duration,
      location: newMeeting.location,
      videoLink: videoLink,
      videoProvider: newMeeting.videoProvider,
      status: 'draft',
      isRecurring: newMeeting.isRecurring,
      recurringPattern: newMeeting.isRecurring ? { frequency: newMeeting.recurringFrequency } : null,
      organizer: '1',
      agenda: template?.agendaItems.map((item, idx) => ({
        id: `ai-new-${idx}`,
        title: item.title,
        duration: item.duration,
        presenter: item.presenter,
        status: 'pending',
        notes: ''
      })) || [],
      attendees: [],
      mom: null,
      actions: [],
      approvals: [],
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };
    
    setMeetings([newMeetingData, ...meetings]);
    setShowNewMeeting(false);
    setNewMeeting({
      title: '',
      committeeId: '',
      templateId: '',
      date: '',
      startTime: '',
      duration: 60,
      location: '',
      videoProvider: '',
      videoLink: '',
      isRecurring: false,
      recurringFrequency: 'weekly'
    });
    toast.success('Meeting created successfully!');
  };

  // Update meeting status
  const updateMeetingStatus = (meetingId, newStatus) => {
    setMeetings(meetings.map(m => 
      m.id === meetingId ? { ...m, status: newStatus, updatedAt: new Date().toISOString().split('T')[0] } : m
    ));
    if (showMeetingDetails?.id === meetingId) {
      setShowMeetingDetails({ ...showMeetingDetails, status: newStatus });
    }
    toast.success(`Meeting status updated to ${getStatusConfig(newStatus).label}`);
  };

  // Add attendee to meeting
  const handleAddAttendee = () => {
    if (!newAttendee.name || !newAttendee.email) return;
    
    const attendee = {
      id: `att-${Date.now()}`,
      odId: newAttendee.isExternal ? null : mockUsers.find(u => u.email === newAttendee.email)?.id || null,
      name: newAttendee.name,
      email: newAttendee.email,
      role: newAttendee.role,
      rsvp: 'pending',
      hasApproved: false,
      isExternal: newAttendee.isExternal,
      company: newAttendee.company
    };
    
    const updatedMeeting = {
      ...showMeetingDetails,
      attendees: [...(showMeetingDetails.attendees || []), attendee]
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    setNewAttendee({ name: '', email: '', role: 'Member', isExternal: false, company: '' });
    toast.success('Attendee added successfully!');
  };

  // Remove attendee from meeting
  const handleRemoveAttendee = (attendeeId) => {
    const updatedMeeting = {
      ...showMeetingDetails,
      attendees: showMeetingDetails.attendees.filter(a => a.id !== attendeeId)
    };
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    toast.success('Attendee removed');
  };

  // Add agenda item
  const handleAddAgendaItem = () => {
    if (!newAgendaItem.title) return;
    
    const agendaItem = {
      id: `agenda-${Date.now()}`,
      title: newAgendaItem.title,
      duration: newAgendaItem.duration,
      presenter: newAgendaItem.presenter,
      status: 'pending',
      notes: ''
    };
    
    const updatedMeeting = {
      ...showMeetingDetails,
      agenda: [...(showMeetingDetails.agenda || []), agendaItem]
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    setNewAgendaItem({ title: '', duration: 15, presenter: '' });
    toast.success('Agenda item added!');
  };

  // Remove agenda item
  const handleRemoveAgendaItem = (agendaId) => {
    const updatedMeeting = {
      ...showMeetingDetails,
      agenda: showMeetingDetails.agenda.filter(a => a.id !== agendaId)
    };
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    toast.success('Agenda item removed');
  };

  // Update agenda item notes
  const updateAgendaNotes = (agendaId, notes) => {
    const updatedMeeting = {
      ...showMeetingDetails,
      agenda: showMeetingDetails.agenda.map(a => 
        a.id === agendaId ? { ...a, notes, status: 'discussed' } : a
      )
    };
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
  };

  // Generate AI MOM
  const generateAIMOM = () => {
    const meeting = showMeetingDetails;
    const committee = mockCommittees.find(c => c.id === meeting.committeeId);
    
    const momText = `**${meeting.title}**
Date: ${formatDate(meeting.date)}
Time: ${formatTime(meeting.startTime)} - ${formatTime(meeting.endTime)}
Location: ${meeting.location || 'Virtual'}

**Attendees:**
${meeting.attendees?.map(a => `- ${a.name} (${a.role})${a.isExternal ? ' [External]' : ''}`).join('\n') || 'No attendees recorded'}

**Agenda Items Discussed:**
${meeting.agenda?.map((a, i) => `${i + 1}. ${a.title}${a.notes ? `\n   Notes: ${a.notes}` : ''}`).join('\n') || 'No agenda items'}

**Key Decisions:**
- [AI Generated] Based on the agenda items, key decisions will be summarized here.

**Action Items:**
${meeting.actions?.map((a, i) => `${i + 1}. ${a.description} - Assigned to: ${mockUsers.find(u => u.id === a.assignedTo)?.name || 'TBD'}, Due: ${formatDate(a.dueDate)}`).join('\n') || 'No action items recorded yet'}

**Next Steps:**
- Follow up on action items by their due dates
- Schedule next ${committee?.name || 'meeting'} session

---
*This MOM was generated using AI and should be reviewed before approval.*`;

    const updatedMeeting = {
      ...showMeetingDetails,
      mom: {
        id: `mom-${Date.now()}`,
        content: momText,
        generatedAt: new Date().toISOString(),
        generatedBy: 'ai',
        approvedBy: [],
        status: 'pending'
      },
      status: 'pending-review'
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    setMomContent(momText);
    toast.success('MOM generated successfully!');
  };

  // Save MOM
  const saveMOM = () => {
    const updatedMeeting = {
      ...showMeetingDetails,
      mom: {
        ...(showMeetingDetails.mom || {}),
        id: showMeetingDetails.mom?.id || `mom-${Date.now()}`,
        content: momContent,
        generatedAt: showMeetingDetails.mom?.generatedAt || new Date().toISOString(),
        generatedBy: showMeetingDetails.mom?.generatedBy || 'manual',
        approvedBy: showMeetingDetails.mom?.approvedBy || [],
        status: 'pending'
      },
      status: showMeetingDetails.status === 'in-progress' ? 'pending-review' : showMeetingDetails.status
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    setEditingMOM(false);
    toast.success('MOM saved successfully!');
  };

  // Approve MOM
  const approveMOM = (userId) => {
    const user = mockUsers.find(u => u.id === userId);
    const updatedApprovals = [
      ...(showMeetingDetails.approvals || []).filter(a => a.odId !== userId),
      { odId: userId, odname: user?.name, approved: true, approvedAt: new Date().toISOString(), comments: '' }
    ];
    
    const allApproved = showMeetingDetails.attendees
      ?.filter(a => !a.isExternal)
      .every(a => updatedApprovals.some(ap => ap.odId === a.odId && ap.approved));
    
    const updatedMeeting = {
      ...showMeetingDetails,
      approvals: updatedApprovals,
      mom: {
        ...showMeetingDetails.mom,
        approvedBy: updatedApprovals.filter(a => a.approved).map(a => a.odId),
        status: allApproved ? 'approved' : 'pending'
      },
      status: allApproved ? 'approved' : 'pending-review'
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    toast.success(allApproved ? 'MOM fully approved!' : 'Your approval recorded');
  };

  // Add action item from meeting
  const handleAddMeetingAction = (description, assignedTo, dueDate) => {
    const newAction = {
      id: `ma-${Date.now()}`,
      description,
      assignedTo,
      dueDate,
      status: 'pending',
      linkedActionItemId: null
    };
    
    const updatedMeeting = {
      ...showMeetingDetails,
      actions: [...(showMeetingDetails.actions || []), newAction]
    };
    
    setMeetings(meetings.map(m => m.id === showMeetingDetails.id ? updatedMeeting : m));
    setShowMeetingDetails(updatedMeeting);
    toast.success('Action item added!');
  };

  // Push actions to Action Items module
  const pushToActionItems = () => {
    toast.success(`${showMeetingDetails.actions?.length || 0} action items pushed to Action Items module!`);
    updateMeetingStatus(showMeetingDetails.id, 'completed');
  };

  // Send meeting invites
  const sendInvites = () => {
    toast.success(`Invitations sent to ${showMeetingDetails.attendees?.length || 0} attendees!`);
    if (showMeetingDetails.status === 'draft') {
      updateMeetingStatus(showMeetingDetails.id, 'scheduled');
    }
  };

  // Delete meeting
  const handleDeleteMeeting = (meetingId) => {
    setMeetings(meetings.filter(m => m.id !== meetingId));
    setShowMeetingDetails(null);
    toast.success('Meeting deleted');
  };

  // Render View Selector
  const renderViewSelector = () => (
    <div className="flex items-center bg-muted/30 rounded-lg p-1">
      {VIEW_OPTIONS.map(view => {
        const Icon = view.icon;
        const isActive = currentView === view.id;
        return (
          <button
            key={view.id}
            onClick={() => setCurrentView(view.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              isActive 
                ? `bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'} text-white shadow-sm` 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {view.label}
          </button>
        );
      })}
    </div>
  );

  // Render Cards View
  const renderCardsView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {filteredMeetings.map(meeting => {
        const committee = mockCommittees.find(c => c.id === meeting.committeeId);
        const statusConfig = getStatusConfig(meeting.status);
        const StatusIcon = statusConfig.icon;
        
        return (
          <Card 
            key={meeting.id}
            className={`border ${currentColorTheme?.cardBorderClass || 'border-border'} hover:shadow-lg transition-all cursor-pointer group`}
            onClick={() => setShowMeetingDetails(meeting)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <Badge className={`${statusConfig.color} border`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowMeetingDetails(meeting); }}>
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteMeeting(meeting.id); }}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <CardTitle className="text-lg mt-2">{meeting.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{committee?.name}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {formatDate(meeting.date)}
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {formatTime(meeting.startTime)}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  {meeting.location && (
                    <Badge variant="outline" className="text-xs">
                      <MapPin className="w-3 h-3 mr-1" />
                      {meeting.location}
                    </Badge>
                  )}
                  {meeting.videoLink && (
                    <Badge variant="outline" className="text-xs text-blue-400">
                      <Video className="w-3 h-3 mr-1" />
                      {meeting.videoProvider === 'zoom' ? 'Zoom' : meeting.videoProvider === 'teams' ? 'Teams' : 'Video'}
                    </Badge>
                  )}
                  {meeting.isRecurring && (
                    <Badge variant="outline" className="text-xs text-purple-400">
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Recurring
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{meeting.attendees?.length || 0} attendees</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ListTodo className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{meeting.actions?.length || 0} actions</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {filteredMeetings.length === 0 && (
        <div className="col-span-full flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Calendar className="w-16 h-16 mb-4 opacity-50" />
          <p>No meetings found</p>
          <Button className="mt-4" onClick={() => setShowNewMeeting(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Schedule a Meeting
          </Button>
        </div>
      )}
    </div>
  );

  // Render Table View
  const renderTableView = () => (
    <div className="p-4">
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="flex items-center px-4 py-3 bg-muted/30 border-b border-border text-sm font-medium text-muted-foreground gap-4">
          <div className="w-[280px] flex-shrink-0">Meeting</div>
          <div className="w-[140px] flex-shrink-0">Date & Time</div>
          <div className="w-[120px] flex-shrink-0">Type</div>
          <div className="w-[100px] flex-shrink-0">Status</div>
          <div className="w-[100px] flex-shrink-0 text-center">Attendees</div>
          <div className="w-[100px] flex-shrink-0 text-center">Actions</div>
          <div className="w-[80px] flex-shrink-0"></div>
        </div>
        <div className="divide-y divide-border">
          {filteredMeetings.map(meeting => {
            const committee = mockCommittees.find(c => c.id === meeting.committeeId);
            const statusConfig = getStatusConfig(meeting.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <div 
                key={meeting.id} 
                className="flex items-center px-4 py-3 hover:bg-muted/20 transition-colors cursor-pointer gap-4"
                onClick={() => setShowMeetingDetails(meeting)}
              >
                <div className="w-[280px] flex-shrink-0">
                  <p className="font-medium text-foreground truncate">{meeting.title}</p>
                  <p className="text-xs text-muted-foreground">{meeting.location || 'No location'}</p>
                </div>
                <div className="w-[140px] flex-shrink-0">
                  <p className="text-sm text-foreground">{formatDate(meeting.date)}</p>
                  <p className="text-xs text-muted-foreground">{formatTime(meeting.startTime)}</p>
                </div>
                <div className="w-[120px] flex-shrink-0">
                  <Badge variant="outline" className="text-xs">{committee?.shortCode || 'N/A'}</Badge>
                </div>
                <div className="w-[100px] flex-shrink-0">
                  <Badge className={`${statusConfig.color} border text-xs`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </div>
                <div className="w-[100px] flex-shrink-0 text-center">
                  <span className="text-sm text-foreground">{meeting.attendees?.length || 0}</span>
                </div>
                <div className="w-[100px] flex-shrink-0 text-center">
                  <span className="text-sm text-foreground">{meeting.actions?.length || 0}</span>
                </div>
                <div className="w-[80px] flex-shrink-0 flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteMeeting(meeting.id); }}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render Calendar View
  const renderCalendarView = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    const getMeetingsForDay = (day) => {
      if (!day) return [];
      const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      return filteredMeetings.filter(m => m.date === dateStr);
    };
    
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div className="p-4">
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="p-4 bg-muted/30 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">{months[currentMonth]} {currentYear}</h3>
          </div>
          <div className="grid grid-cols-7">
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground border-b border-border">
                {day}
              </div>
            ))}
            {days.map((day, index) => {
              const dayMeetings = getMeetingsForDay(day);
              const isToday = day === today.getDate();
              return (
                <div 
                  key={index} 
                  className={`min-h-[100px] p-2 border-b border-r border-border ${!day ? 'bg-muted/10' : ''} ${isToday ? 'bg-primary/5' : ''}`}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : 'text-foreground'}`}>{day}</div>
                      <div className="space-y-1">
                        {dayMeetings.slice(0, 2).map(meeting => {
                          const statusConfig = getStatusConfig(meeting.status);
                          return (
                            <div 
                              key={meeting.id}
                              className={`text-xs p-1 rounded truncate cursor-pointer ${statusConfig.color}`}
                              onClick={() => setShowMeetingDetails(meeting)}
                            >
                              {formatTime(meeting.startTime)} {meeting.title}
                            </div>
                          );
                        })}
                        {dayMeetings.length > 2 && (
                          <div className="text-xs text-muted-foreground">+{dayMeetings.length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Timeline View
  const renderTimelineView = () => (
    <div className="p-4">
      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />
        <div className="space-y-6">
          {filteredMeetings.map((meeting, index) => {
            const committee = mockCommittees.find(c => c.id === meeting.committeeId);
            const statusConfig = getStatusConfig(meeting.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <div key={meeting.id} className="relative flex items-start gap-4 ml-4">
                <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}>
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <Card 
                  className={`flex-1 border ${currentColorTheme?.cardBorderClass || 'border-border'} cursor-pointer hover:shadow-md transition-shadow`}
                  onClick={() => setShowMeetingDetails(meeting)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm text-muted-foreground">{formatDate(meeting.date)} at {formatTime(meeting.startTime)}</p>
                        <h4 className="font-semibold text-foreground">{meeting.title}</h4>
                        <p className="text-sm text-muted-foreground">{committee?.name}</p>
                      </div>
                      <Badge className={`${statusConfig.color} border`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {meeting.attendees?.length || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <ListTodo className="w-4 h-4" />
                        {meeting.actions?.length || 0}
                      </div>
                      {meeting.videoLink && (
                        <div className="flex items-center gap-1 text-blue-400">
                          <Video className="w-4 h-4" />
                          {meeting.videoProvider}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render New Meeting Dialog
  const renderNewMeetingDialog = () => (
    <Dialog open={showNewMeeting} onOpenChange={setShowNewMeeting}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Schedule New Meeting
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Meeting Title</Label>
            <Input 
              placeholder="e.g., Q4 Board Meeting"
              value={newMeeting.title}
              onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
              data-testid="meeting-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select value={newMeeting.committeeId} onValueChange={(v) => setNewMeeting({...newMeeting, committeeId: v})}>
                <SelectTrigger data-testid="meeting-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {mockCommittees.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Use Template</Label>
              <Select value={newMeeting.templateId} onValueChange={(v) => setNewMeeting({...newMeeting, templateId: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input 
                type="date"
                value={newMeeting.date}
                onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
                className="bg-background text-foreground [color-scheme:dark]"
                data-testid="meeting-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input 
                type="time"
                value={newMeeting.startTime}
                onChange={(e) => setNewMeeting({...newMeeting, startTime: e.target.value})}
                className="bg-background text-foreground [color-scheme:dark]"
                data-testid="meeting-time-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={newMeeting.duration.toString()} onValueChange={(v) => setNewMeeting({...newMeeting, duration: parseInt(v)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 mins</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <Input 
              placeholder="e.g., Board Room A"
              value={newMeeting.location}
              onChange={(e) => setNewMeeting({...newMeeting, location: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label>Video Conference</Label>
            <div className="grid grid-cols-2 gap-4">
              <Select value={newMeeting.videoProvider} onValueChange={(v) => setNewMeeting({...newMeeting, videoProvider: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="zoom">Zoom (Auto-generate link)</SelectItem>
                  <SelectItem value="teams">Microsoft Teams (Auto-generate link)</SelectItem>
                  <SelectItem value="meet">Google Meet (Auto-generate link)</SelectItem>
                </SelectContent>
              </Select>
              {newMeeting.videoProvider && (
                <div className="flex items-center gap-2 text-sm text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  Link will be generated automatically
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Recurring Meeting</p>
                <p className="text-xs text-muted-foreground">Schedule this meeting to repeat</p>
              </div>
            </div>
            <Switch 
              checked={newMeeting.isRecurring}
              onCheckedChange={(v) => setNewMeeting({...newMeeting, isRecurring: v})}
            />
          </div>

          {newMeeting.isRecurring && (
            <div className="space-y-2">
              <Label>Repeat</Label>
              <Select value={newMeeting.recurringFrequency} onValueChange={(v) => setNewMeeting({...newMeeting, recurringFrequency: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowNewMeeting(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateMeeting} 
            disabled={!newMeeting.title || !newMeeting.committeeId || !newMeeting.date}
            data-testid="create-meeting-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Meeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Render Meeting Details Dialog
  const renderMeetingDetailsDialog = (meeting) => {
    const committee = mockCommittees.find(c => c.id === meeting.committeeId);
    const statusConfig = getStatusConfig(meeting.status);
    const StatusIcon = statusConfig.icon;
    
    return (
      <Dialog open={!!showMeetingDetails} onOpenChange={() => setShowMeetingDetails(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl">{meeting.title}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {committee?.name} | {formatDate(meeting.date)} at {formatTime(meeting.startTime)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${statusConfig.color} border`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Change Status
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {Object.values(meetingStatuses).map(status => (
                      <DropdownMenuItem 
                        key={status} 
                        onClick={() => updateMeetingStatus(meeting.id, status)}
                        disabled={meeting.status === status}
                      >
                        {getStatusConfig(status).label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="flex-1 overflow-hidden flex flex-col mt-4">
            <TabsList className="grid w-full grid-cols-5 flex-shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="agenda">Agenda</TabsTrigger>
              <TabsTrigger value="attendees">Attendees</TabsTrigger>
              <TabsTrigger value="mom">MOM</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 m-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Duration</span>
                    </div>
                    <p className="font-medium">{meeting.duration} minutes</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">Location</span>
                    </div>
                    <p className="font-medium">{meeting.location || 'Not specified'}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">Attendees</span>
                    </div>
                    <p className="font-medium">{meeting.attendees?.length || 0} people</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <ListTodo className="w-4 h-4" />
                      <span className="text-sm">Action Items</span>
                    </div>
                    <p className="font-medium">{meeting.actions?.length || 0} items</p>
                  </div>
                </div>

                {meeting.videoLink && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium">
                          {meeting.videoProvider === 'zoom' ? 'Zoom Meeting' : 
                           meeting.videoProvider === 'teams' ? 'Microsoft Teams' : 'Video Call'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => {navigator.clipboard.writeText(meeting.videoLink); toast.success('Link copied!');}}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => window.open(meeting.videoLink, '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Join
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-4">
                  <Button 
                    className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                    onClick={sendInvites}
                    data-testid="send-invites-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Invites
                  </Button>
                  {meeting.status === 'scheduled' && (
                    <Button variant="outline" onClick={() => updateMeetingStatus(meeting.id, 'in-progress')}>
                      <Play className="w-4 h-4 mr-2" />
                      Start Meeting
                    </Button>
                  )}
                  {meeting.status === 'in-progress' && (
                    <Button variant="outline" onClick={() => { setActiveDetailTab('mom'); }}>
                      <FileText className="w-4 h-4 mr-2" />
                      Take MOM
                    </Button>
                  )}
                </div>
              </TabsContent>

              {/* Agenda Tab */}
              <TabsContent value="agenda" className="space-y-4 m-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Meeting Agenda</h4>
                  <Button variant="outline" size="sm" onClick={() => setEditingAgenda(!editingAgenda)}>
                    {editingAgenda ? 'Done' : 'Edit Agenda'}
                  </Button>
                </div>
                
                {editingAgenda && (
                  <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <Input 
                        placeholder="Agenda item title"
                        value={newAgendaItem.title}
                        onChange={(e) => setNewAgendaItem({...newAgendaItem, title: e.target.value})}
                      />
                      <Select value={newAgendaItem.duration.toString()} onValueChange={(v) => setNewAgendaItem({...newAgendaItem, duration: parseInt(v)})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5 min</SelectItem>
                          <SelectItem value="10">10 min</SelectItem>
                          <SelectItem value="15">15 min</SelectItem>
                          <SelectItem value="20">20 min</SelectItem>
                          <SelectItem value="30">30 min</SelectItem>
                          <SelectItem value="45">45 min</SelectItem>
                          <SelectItem value="60">60 min</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={newAgendaItem.presenter} onValueChange={(v) => setNewAgendaItem({...newAgendaItem, presenter: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Presenter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No presenter</SelectItem>
                          {mockUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" onClick={handleAddAgendaItem} disabled={!newAgendaItem.title}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  {meeting.agenda?.map((item, index) => {
                    const presenter = mockUsers.find(u => u.id === item.presenter);
                    return (
                      <div key={item.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg group">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{item.title}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                <Timer className="w-3 h-3 mr-1" />
                                {item.duration} min
                              </Badge>
                              {item.status === 'discussed' && (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 border">
                                  <Check className="w-3 h-3 mr-1" />
                                  Discussed
                                </Badge>
                              )}
                              {editingAgenda && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => handleRemoveAgendaItem(item.id)}>
                                  <Trash2 className="w-3 h-3 text-red-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {presenter && (
                            <p className="text-sm text-muted-foreground mt-1">Presenter: {presenter.name}</p>
                          )}
                          {meeting.status === 'in-progress' && (
                            <Textarea 
                              placeholder="Add notes for this agenda item..."
                              className="mt-2 text-sm"
                              value={item.notes || ''}
                              onChange={(e) => updateAgendaNotes(item.id, e.target.value)}
                            />
                          )}
                          {item.notes && meeting.status !== 'in-progress' && (
                            <p className="text-sm text-muted-foreground mt-2 bg-muted/50 p-2 rounded">{item.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(!meeting.agenda || meeting.agenda.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No agenda items yet</p>
                  )}
                </div>
              </TabsContent>

              {/* Attendees Tab */}
              <TabsContent value="attendees" className="space-y-4 m-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Meeting Attendees</h4>
                  <Button variant="outline" size="sm" onClick={() => setEditingAttendees(!editingAttendees)}>
                    {editingAttendees ? 'Done' : 'Manage Attendees'}
                  </Button>
                </div>

                {editingAttendees && (
                  <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={newAttendee.isExternal}
                          onCheckedChange={(v) => setNewAttendee({...newAttendee, isExternal: v})}
                        />
                        <Label className="text-sm">{newAttendee.isExternal ? 'External Guest' : 'Internal Member'}</Label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {!newAttendee.isExternal ? (
                        <Select onValueChange={(v) => {
                          const user = mockUsers.find(u => u.id === v);
                          if (user) setNewAttendee({...newAttendee, name: user.name, email: user.email});
                        }}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select team member" />
                          </SelectTrigger>
                          <SelectContent>
                            {mockUsers.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input 
                          placeholder="Full name"
                          value={newAttendee.name}
                          onChange={(e) => setNewAttendee({...newAttendee, name: e.target.value})}
                        />
                      )}
                      <Input 
                        placeholder="Email address"
                        type="email"
                        value={newAttendee.email}
                        onChange={(e) => setNewAttendee({...newAttendee, email: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={newAttendee.role} onValueChange={(v) => setNewAttendee({...newAttendee, role: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Chair">Chair</SelectItem>
                          <SelectItem value="Secretary">Secretary</SelectItem>
                          <SelectItem value="Member">Member</SelectItem>
                          <SelectItem value="Guest">Guest</SelectItem>
                          <SelectItem value="Presenter">Presenter</SelectItem>
                        </SelectContent>
                      </Select>
                      {newAttendee.isExternal && (
                        <Input 
                          placeholder="Company (optional)"
                          value={newAttendee.company}
                          onChange={(e) => setNewAttendee({...newAttendee, company: e.target.value})}
                        />
                      )}
                    </div>
                    <Button size="sm" onClick={handleAddAttendee} disabled={!newAttendee.name || !newAttendee.email}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Attendee
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  {meeting.attendees?.map(attendee => (
                    <div key={attendee.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg group">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`text-white ${attendee.isExternal ? 'bg-gradient-to-r from-amber-500 to-orange-600' : 'bg-gradient-to-r from-violet-500 to-purple-600'}`}>
                            {attendee.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{attendee.name}</p>
                          <p className="text-sm text-muted-foreground">{attendee.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{attendee.role}</Badge>
                        {attendee.isExternal && (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 border">
                            <Globe className="w-3 h-3 mr-1" />
                            External
                          </Badge>
                        )}
                        {attendee.rsvp === 'accepted' && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 border">
                            <Check className="w-3 h-3 mr-1" />
                            Accepted
                          </Badge>
                        )}
                        {attendee.rsvp === 'pending' && (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">Pending</Badge>
                        )}
                        {editingAttendees && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={() => handleRemoveAttendee(attendee.id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!meeting.attendees || meeting.attendees.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No attendees added yet</p>
                  )}
                </div>
              </TabsContent>

              {/* MOM Tab */}
              <TabsContent value="mom" className="space-y-4 m-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Minutes of Meeting</h4>
                  <div className="flex items-center gap-2">
                    {!meeting.mom && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={generateAIMOM}
                        data-testid="generate-mom-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                        Generate with AI
                      </Button>
                    )}
                    {meeting.mom && !editingMOM && (
                      <Button variant="outline" size="sm" onClick={() => { setMomContent(meeting.mom.content); setEditingMOM(true); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit MOM
                      </Button>
                    )}
                  </div>
                </div>

                {editingMOM ? (
                  <div className="space-y-3">
                    <Textarea 
                      value={momContent}
                      onChange={(e) => setMomContent(e.target.value)}
                      className="min-h-[300px] font-mono text-sm"
                      placeholder="Enter meeting minutes..."
                    />
                    <div className="flex items-center gap-2">
                      <Button onClick={saveMOM}>
                        <Save className="w-4 h-4 mr-2" />
                        Save MOM
                      </Button>
                      <Button variant="outline" onClick={() => setEditingMOM(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : meeting.mom ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm font-medium">Meeting Summary</span>
                        </div>
                        <Badge className={meeting.mom.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}>
                          {meeting.mom.status === 'approved' ? 'Approved' : 'Pending Approval'}
                        </Badge>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm text-foreground">{meeting.mom.content}</pre>
                    </div>

                    {/* Approval Section */}
                    {meeting.status === 'pending-review' && (
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4" />
                          Approvals Required
                        </h4>
                        <div className="space-y-2">
                          {meeting.attendees?.filter(a => !a.isExternal).map(attendee => {
                            const approval = meeting.approvals?.find(ap => ap.odId === attendee.odId);
                            return (
                              <div key={attendee.id} className="flex items-center justify-between p-2 bg-background/50 rounded">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback className="text-xs bg-gradient-to-r from-violet-500 to-purple-600 text-white">
                                      {attendee.name.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{attendee.name}</span>
                                </div>
                                {approval?.approved ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 border">
                                    <Check className="w-3 h-3 mr-1" />
                                    Approved
                                  </Badge>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => approveMOM(attendee.odId)}
                                    data-testid={`approve-mom-${attendee.odId}`}
                                  >
                                    <ThumbsUp className="w-3 h-3 mr-1" />
                                    Approve
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No MOM generated yet</p>
                    <div className="flex items-center justify-center gap-2">
                      <Button onClick={generateAIMOM}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate with AI
                      </Button>
                      <Button variant="outline" onClick={() => { setMomContent(''); setEditingMOM(true); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Write Manually
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="space-y-4 m-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Action Items ({meeting.actions?.length || 0})</h4>
                  {meeting.status === 'approved' && meeting.actions?.length > 0 && (
                    <Button 
                      size="sm" 
                      className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                      onClick={pushToActionItems}
                      data-testid="push-actions-btn"
                    >
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      Push to Action Items
                    </Button>
                  )}
                </div>

                {/* Add New Action */}
                <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                  <h5 className="text-sm font-medium">Add Action Item</h5>
                  <div className="grid grid-cols-3 gap-3">
                    <Textarea 
                      placeholder="Action item description"
                      className="col-span-3 min-h-[60px]"
                      id="new-action-description"
                    />
                    <Select id="new-action-assignee">
                      <SelectTrigger>
                        <SelectValue placeholder="Assign to" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="date" id="new-action-due" className="[color-scheme:dark]" />
                    <Button 
                      size="sm"
                      onClick={() => {
                        const desc = document.getElementById('new-action-description').value;
                        const assignee = document.querySelector('#new-action-assignee button')?.textContent;
                        const due = document.getElementById('new-action-due').value;
                        const assigneeId = mockUsers.find(u => u.name === assignee)?.id;
                        if (desc && assigneeId && due) {
                          handleAddMeetingAction(desc, assigneeId, due);
                          document.getElementById('new-action-description').value = '';
                          document.getElementById('new-action-due').value = '';
                        }
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {meeting.actions?.map(action => {
                    const assignee = mockUsers.find(u => u.id === action.assignedTo);
                    return (
                      <div key={action.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Checkbox checked={action.status === 'completed'} />
                          <div>
                            <p className={`text-sm ${action.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {action.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {assignee?.name} | Due: {formatDate(action.dueDate)}
                            </p>
                          </div>
                        </div>
                        <Badge className={action.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}>
                          {action.status}
                        </Badge>
                      </div>
                    );
                  })}
                  {(!meeting.actions || meeting.actions.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No action items yet</p>
                  )}
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    );
  };

  // Render Dashboard
  if (showDashboard) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-orange-500 via-amber-500 to-yellow-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            <div className="p-6 space-y-6">
              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowNewMeeting(true)}
                  className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                  data-testid="new-meeting-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Meeting
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Total</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600">
                        <CalendarDays className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-foreground">{analytics.total}</p>
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Upcoming</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-green-500">{analytics.upcoming}</p>
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Pending</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600">
                        <AlertCircle className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-orange-500">{analytics.pendingApproval}</p>
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Completed</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-emerald-500">{analytics.completed}</p>
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Actions</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600">
                        <ListTodo className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-violet-500">{analytics.totalActions}</p>
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Action Rate</span>
                      <div className="p-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-600">
                        <Target className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-pink-500">{analytics.actionCompletionRate}%</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Upcoming Meetings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {meetings
                      .filter(m => m.date >= new Date().toISOString().split('T')[0] && !['completed', 'cancelled'].includes(m.status))
                      .slice(0, 4)
                      .map(meeting => {
                        const committee = mockCommittees.find(c => c.id === meeting.committeeId);
                        const statusConfig = getStatusConfig(meeting.status);
                        return (
                          <div 
                            key={meeting.id} 
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => setShowMeetingDetails(meeting)}
                            data-testid={`meeting-${meeting.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-blue-500 to-cyan-600'}`}>
                                <Calendar className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{meeting.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {committee?.name} | {formatDate(meeting.date)} at {formatTime(meeting.startTime)}
                                </p>
                              </div>
                            </div>
                            <Badge className={`${statusConfig.color} border`}>
                              {statusConfig.label}
                            </Badge>
                          </div>
                        );
                      })}
                    {meetings.filter(m => m.date >= new Date().toISOString().split('T')[0]).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No upcoming meetings</p>
                    )}
                  </CardContent>
                </Card>

                <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                      Pending Approvals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {meetings
                      .filter(m => m.status === 'pending-review')
                      .map(meeting => {
                        const committee = mockCommittees.find(c => c.id === meeting.committeeId);
                        const approvedCount = meeting.approvals?.filter(a => a.approved).length || 0;
                        const totalAttendees = meeting.attendees?.filter(a => !a.isExternal).length || 0;
                        return (
                          <div 
                            key={meeting.id} 
                            className="p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => setShowMeetingDetails(meeting)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-foreground">{meeting.title}</p>
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 border">
                                {approvedCount}/{totalAttendees} Approved
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {committee?.name} | {formatDate(meeting.date)}
                            </p>
                            <Progress 
                              value={totalAttendees > 0 ? (approvedCount / totalAttendees) * 100 : 0} 
                              className="h-1.5"
                            />
                          </div>
                        );
                      })}
                    {meetings.filter(m => m.status === 'pending-review').length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No pending approvals</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Meetings by Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {mockCommittees.map((committee, index) => {
                      const count = meetings.filter(m => m.committeeId === committee.id).length;
                      const maxCount = Math.max(...mockCommittees.map(c => meetings.filter(m => m.committeeId === c.id).length), 1);
                      const percentage = (count / maxCount) * 100;
                      const colors = ['from-violet-500 to-purple-600', 'from-blue-500 to-cyan-600', 'from-emerald-500 to-green-600', 'from-amber-500 to-orange-600', 'from-rose-500 to-pink-600', 'from-indigo-500 to-blue-600'];
                      return (
                        <div key={committee.id} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground">{committee.name}</span>
                            <span className="text-muted-foreground">{count} meetings</span>
                          </div>
                          <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {renderNewMeetingDialog()}
            {showMeetingDetails && renderMeetingDetailsDialog(showMeetingDetails)}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Render Templates Page
  if (showTemplates) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-orange-500 via-amber-500 to-yellow-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="text-4xl font-bold text-white">{templates.length}</p>
                  <p className="text-white/80 text-sm">Templates</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => {
                  const committee = mockCommittees.find(c => c.id === template.committeeId);
                  return (
                    <Card key={template.id} className={`border ${currentColorTheme?.cardBorderClass || 'border-border'}`}>
                      <CardHeader>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{committee?.name} | {template.duration} minutes</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {template.agendaItems.slice(0, 4).map((item, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm">
                              <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs">
                                {index + 1}
                              </div>
                              <span className="text-muted-foreground truncate">{item.title}</span>
                            </div>
                          ))}
                          {template.agendaItems.length > 4 && (
                            <p className="text-xs text-muted-foreground">+{template.agendaItems.length - 4} more items</p>
                          )}
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button variant="outline" size="sm" className="flex-1">
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1"
                            onClick={() => {
                              setNewMeeting({...newMeeting, templateId: template.id, committeeId: template.committeeId});
                              setShowNewMeeting(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Use
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {renderNewMeetingDialog()}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Main List View with View Selector
  return (
    <TooltipProvider>
      <div className="flex h-full">
        {showFilters && (
          <div className="w-64 border-r border-border bg-card p-4 space-y-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>

            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Meeting Type
                <ChevronDown className="w-4 h-4" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pt-2">
                {mockCommittees.map(committee => (
                  <div key={committee.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`type-${committee.id}`}
                      checked={selectedTypes.includes(committee.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedTypes([...selectedTypes, committee.id]);
                        } else {
                          setSelectedTypes(selectedTypes.filter(t => t !== committee.id));
                        }
                      }}
                    />
                    <label htmlFor={`type-${committee.id}`} className="text-sm cursor-pointer">
                      {committee.name}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>

            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Status
                <ChevronDown className="w-4 h-4" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pt-2">
                {Object.values(meetingStatuses).map(status => (
                  <div key={status} className="flex items-center gap-2">
                    <Checkbox
                      id={`status-${status}`}
                      checked={selectedStatuses.includes(status)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedStatuses([...selectedStatuses, status]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                        }
                      }}
                    />
                    <label htmlFor={`status-${status}`} className="text-sm cursor-pointer">
                      {getStatusConfig(status).label}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-cyan-500 via-blue-500 to-indigo-500'} p-8`}>
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                <p className="text-white/80">{pageInfo.subtitle}</p>
              </div>
              <div className="text-right mr-4">
                <p className="text-4xl font-bold text-white">{filteredMeetings.length}</p>
                <p className="text-white/80 text-sm">Total Meetings</p>
              </div>
            </div>
          </div>
          <HeaderGlow />

          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search meetings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-meetings-input"
                />
              </div>
              {renderViewSelector()}
            </div>
            <Button 
              onClick={() => setShowNewMeeting(true)}
              className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
              data-testid="new-meeting-btn-list"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Meeting
            </Button>
          </div>

          {currentView === 'cards' && renderCardsView()}
          {currentView === 'table' && renderTableView()}
          {currentView === 'calendar' && renderCalendarView()}
          {currentView === 'timeline' && renderTimelineView()}
        </div>

        {renderNewMeetingDialog()}
        {showMeetingDetails && renderMeetingDetailsDialog(showMeetingDetails)}
      </div>
    </TooltipProvider>
  );
};

export default MeetingManagement;
